import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {addMonths, addYears} from "date-fns";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";

import useLlmData from "../../../hooks/useLlmData";
import { MarkdownViewer } from "./MarkDownViewer";
import Loading from "../../Utils/Loading";
import { buildChartConfigs } from "../../AiComponents/ChartRegistry";
import SimmsDatePicker from "../../datepicker/SimmsDatePicker";
import { toKSTISOString, toKSTISOStringEnd } from "../../commonmethod/CommonMethod";
import { closeKeyAndFlush, openAiDrawer } from "../../../redux/slice/opnerSlice";
import Close from "../../../resources/icon/close-white.png";

const defaultStart = () => toKSTISOString(addYears(new Date(), -1));
const defaultEnd = () => toKSTISOStringEnd(addYears(new Date(), 0));
const DEFAULT_STATUS_TEXT = "요약 생성 중..";

function AiDrawer({ keys }) {
    const open = useSelector((state) => state.opener.drawer["AI"] || false);
    const openerData = useSelector((state) => state.opener.aiDrawerData);
    const dispatch = useDispatch();

    const isCompact = open && !openerData;
    const [startDate, setStartDate] = useState(defaultStart());
    const [endDate, setEndDate] = useState(defaultEnd());

    useEffect(() => {
        if (open && !openerData) {
            setStartDate(defaultStart());
            setEndDate(defaultEnd());
        }
    }, [open, openerData]);

    const effectiveKeys = open && openerData?.keys ? openerData.keys : keys;
    const entityName = effectiveKeys;

    const search = useMemo(() => {
        return openerData?.startDate && openerData?.endDate
            ? { startDate: openerData.startDate, endDate: openerData.endDate, entityName }
            : undefined;
    }, [entityName, openerData?.startDate, openerData?.endDate]);

    const enabled = open && !!openerData && !!search?.startDate && !!search?.endDate;
    const { data: message, loading, error, statusText } = useLlmData({
        keys: effectiveKeys,
        enabled,
        search,
    });

    const isDone = !loading && !!message && !error;
    const visibleStatusText = isDone ? "" : statusText || (loading ? DEFAULT_STATUS_TEXT : "");

    const scrollRef = useRef(null);
    const bottomRef = useRef(null);
    const printAreaRef = useRef(null);

    const chartConfigs = useMemo(() => buildChartConfigs(effectiveKeys), [effectiveKeys]);

    const markdownForViewer = useMemo(() => {
        return message || "";
    }, [message]);

    useLayoutEffect(() => {
        if (!open || !openerData || !message) return;

        const raf = requestAnimationFrame(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
            if (bottomRef.current) {
                bottomRef.current.scrollIntoView({ behavior: "auto", block: "end" });
            }
        });

        return () => cancelAnimationFrame(raf);
    }, [open, openerData, message]);

    const onPrint = useCallback(() => {
        const source = printAreaRef.current;
        if (!source) return;

        const printContainer = document.createElement("div");
        printContainer.className = "ai-print-container";

        const clone = source.cloneNode(true);
        clone.querySelectorAll("[data-no-print]").forEach((n) => n.remove());
        clone
            .querySelectorAll(
                ".recharts-tooltip-wrapper, [class*='recharts-tooltip'], .recharts-active-shape, .recharts-active-dot"
            )
            .forEach((n) => n.remove());

        const svgs = clone.querySelectorAll("svg");
        svgs.forEach((svg) => {
            const parent = svg.parentElement;
            if (!parent) return;

            let w = svg.getAttribute("width");
            let h = svg.getAttribute("height");

            if (!w || !h) {
                const vb = svg.getAttribute("viewBox");
                if (vb) {
                    const parts = vb.split(/\s+/).map(Number);
                    if (parts.length === 4) {
                        w = String(parts[2]);
                        h = String(parts[3]);
                    }
                }
            }

            if (!w) w = "800";
            if (!h) h = "320";

            svg.setAttribute("width", w);
            svg.setAttribute("height", h);
            if (!svg.getAttribute("xmlns")) {
                svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
            }

            const svgText = new XMLSerializer().serializeToString(svg);
            const dataUri = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgText);

            const img = document.createElement("img");
            img.src = dataUri;
            img.alt = "chart";
            img.style.width = "100%";
            img.style.height = "auto";
            img.style.display = "block";

            parent.replaceChild(img, svg);
        });

        printContainer.appendChild(clone);
        document.body.appendChild(printContainer);
        document.body.classList.add("ai-print-mode");

        const cleanup = () => {
            document.body.classList.remove("ai-print-mode");
            if (printContainer.parentElement) {
                printContainer.parentElement.removeChild(printContainer);
            }
            window.removeEventListener("afterprint", cleanup);
        };

        window.addEventListener("afterprint", cleanup);
        setTimeout(() => window.print(), 0);
    }, []);

    const showTopBar = !!openerData && (!!visibleStatusText || isDone || error);

    const handleConfirmDate = () => {
        dispatch(openAiDrawer({ data: { keys, startDate, endDate } }));
    };

    return (
        <div
            ref={scrollRef}
            className={`drawer-view2 p-[1.5rem] ${open ? "slide-in2" : ""} overflow-auto scroll-hidden`}
            style={{
                width: isCompact ? "375px" : "35%",
                height: isCompact ? "auto" : undefined,
                maxHeight: isCompact ? "380px" : undefined,
                transition:
                    "width 0.5s cubic-bezier(0.25, 0.1, 0.25, 1), max-height 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)",
            }}
        >
            {isCompact ? (
                <div className="pt-1">
                    <div className="mb-3 text-[16px] text-[#374151]">기간을 선택해주세요</div>
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <SimmsDatePicker
                            onChange={(v) => setStartDate(v || defaultStart())}
                            data={startDate}
                            constraint={{ lessThan: endDate ? dayjs(endDate) : undefined }}
                            auth={true}
                        />
                        <span className="text-[#6b7280]">~</span>
                        <SimmsDatePicker
                            onChange={(v) => setEndDate(v || defaultEnd())}
                            data={endDate}
                            constraint={{ largerThan: startDate ? dayjs(startDate) : undefined }}
                            auth={true}
                        />
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => dispatch(closeKeyAndFlush({ key: "AI" }))}
                            className="rounded border border-[rgba(0,0,0,0.12)] bg-white px-3 py-2 text-[12px] text-[#374151] hover:bg-gray-50"
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirmDate}
                            className="rounded bg-[var(--main-bg-color)] px-3 py-2 text-[12px] text-white hover:opacity-90"
                        >
                            질문
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div ref={printAreaRef}>
                        {showTopBar ? (
                            <div
                                data-no-print
                                className="mb-3 flex flex-col gap-2 rounded-[3px] border border-[rgba(0,0,0,0.06)] bg-[#f3f6fb] px-3 py-2 text-[#374151]"
                            >
                                <div className={"flex items-center justify-between"}>
                                    <div className="min-w-0 pr-2 text-[13px]">
                                        {!visibleStatusText ? (
                                            <>
                                                {(search?.startDate || "").substring(0, 10)} ~ {(search?.endDate || "").substring(0, 10)}
                                            </>
                                        ) : null}
                                    </div>

                                    <img
                                        src={Close}
                                        className={"w-[16px]"}
                                        onClick={() => dispatch(closeKeyAndFlush({ key: "AI" }))}
                                    />
                                </div>

                                <div className={"flex items-center justify-between"}>
                                    <div className="min-w-0 pr-2">{error ? statusText : visibleStatusText}</div>
                                    {isDone ? (
                                        <button
                                            type="button"
                                            onClick={onPrint}
                                            className="h-7 shrink-0 rounded-[3px] border border-[rgba(0,0,0,0.12)] bg-white px-2 text-[12px] text-[#111827] hover:bg-gray-50"
                                            title="요약 보고서를 출력합니다"
                                        >
                                            프린트
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        {!message ? (
                            <Loading />
                        ) : (
                            <MarkdownViewer content={markdownForViewer} chartConfigs={chartConfigs} />
                        )}
                    </div>
                    <div ref={bottomRef} />
                </>
            )}
        </div>
    );
}

export default AiDrawer;
